import React, { useState } from "react";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import SectionHeader from "../components/common/SectionHeader";
import EmployeesTab from "../components/employees/EmployeesTab";
import LocationsTab from "../components/locations/LocationsTab";
import PcpConfigTab from "../components/pcp-config/PcpConfigTab";
import RolesTab from "../components/roles/RolesTab";

const SECTIONS = {
  MASTER_DATA: "masterData",
  USERS_ROLES: "usersRoles",
};

const PAGES = {
  EMPLOYEES: "employees",
  LOCATIONS: "locations",
  PCP_CONFIG: "pcpConfig",
  ROLES: "roles",
};

const MENU_CONFIG = [
  {
    section: SECTIONS.MASTER_DATA,
    label: "Master Data",
    description: "Employee & location registry",
    icon: StorageOutlinedIcon,
    items: [
      { key: PAGES.EMPLOYEES, label: "Employees", icon: BadgeOutlinedIcon },
      { key: PAGES.LOCATIONS, label: "Locations", icon: LocationOnOutlinedIcon },
      { key: PAGES.PCP_CONFIG, label: "PCP Configuration", icon: LocalHospitalOutlinedIcon },
    ],
  },
  {
    section: SECTIONS.USERS_ROLES,
    label: "Roles & Permissions",
    description: "Access control & role assignment",
    icon: ManageAccountsOutlinedIcon,
    items: [
      { key: PAGES.ROLES, label: "Roles & Permissions", icon: SecurityOutlinedIcon },
    ],
  },
];

const SIDEBAR_WIDTH = 230;

const AdministrationPage = () => {
  const [activePage, setActivePage] = useState(PAGES.EMPLOYEES);

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        icon={AdminPanelSettingsIcon}
        title="Administration"
        description="Manage employees, locations, user accounts, and role assignments."
        iconColor="#ef5350"
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Sidebar navigation */}
        <Paper
          sx={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            flexShrink: 0,
            alignSelf: "flex-start",
            position: "sticky",
            top: 80,
          }}
        >
          {MENU_CONFIG.map((section, idx) => (
            <Box key={section.section}>
              {idx > 0 && <Divider />}
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <section.icon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    {section.label}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                  {section.description}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ pb: 0.5 }}>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.key}
                    selected={activePage === item.key}
                    onClick={() => setActivePage(item.key)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.25,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "& .MuiListItemIcon-root": { color: "primary.contrastText" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <item.icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}
        </Paper>

        {/* Content area */}
        <Paper sx={{ flex: 1, p: 2, minHeight: 400 }}>
          {activePage === PAGES.EMPLOYEES && <EmployeesTab />}
          {activePage === PAGES.LOCATIONS && <LocationsTab />}
          {activePage === PAGES.PCP_CONFIG && <PcpConfigTab />}
          {activePage === PAGES.ROLES && <RolesTab />}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdministrationPage;
